import { GuildRank, SocialAction, type SocialPacket } from '@game/protocol';

import { collapsiblePanel } from './panel.ts';

/**
 * Lista de amigos e guilda (PRD §6).
 *
 * O que esta janela responde é uma pergunta só — **quem está online agora** —, e é por isso
 * que amigos e guilda dividem o painel: a resposta vem do mesmo lugar e muda pelos mesmos
 * eventos. A presença atravessa instâncias pelo barramento Redis, então um amigo no processo
 * ao lado acende aqui do mesmo jeito que um na mesma região.
 *
 * O convite de guilda aparece **dentro** da janela, e não como uma mensagem no chat, porque
 * mensagem rola para fora da tela: um convite que exige resposta precisa continuar visível
 * até ser respondido.
 */
export interface SocialHandlers {
  onAction(action: SocialAction, name: string): void;
}

export class SocialPanel {
  private readonly root: HTMLElement;
  private readonly handlers: SocialHandlers;
  private state: SocialPacket | null = null;
  private open = false;

  constructor(root: HTMLElement, handlers: SocialHandlers) {
    this.root = root;
    this.handlers = handlers;
    this.root.className = 'social';
    this.render();
  }

  setState(packet: SocialPacket): void {
    this.state = packet;
    this.render();
  }

  toggle(): void {
    this.open = !this.open;
    this.render();
  }

  clear(): void {
    this.state = null;
    this.render();
  }

  /**
   * A caixa é o **próprio** `#social`, e não um `<section>` dentro dele.
   *
   * O envelope extra existia por hábito e passou a atrapalhar quando o painel ganhou barra de
   * título: a regra que esconde o conteúdo recolhido é `> *:not(.panel-title)`, e ela só
   * alcança filhos diretos de quem carrega a classe `collapsed`. Com o `<section>` no meio, a
   * classe cairia nele e o piso de altura ficaria no elemento de fora — o painel recolheria e
   * a coluna continuaria reservando os 92 px de `--piso-social` para uma barra de título.
   */
  private render(): void {
    this.root.innerHTML = '';
    this.root.classList.toggle('visible', this.open);
    if (!this.open) return;

    const state = this.state;

    const header = collapsiblePanel(this.root, 'social', 'Amigos e guilda');
    this.root.append(header.root);

    if (!state) {
      const empty = document.createElement('p');
      empty.className = 'social-empty';
      empty.textContent = 'carregando…';
      this.root.append(empty);
      return;
    }

    // Convite primeiro: é a única linha que espera uma resposta.
    if (state.invite) {
      const invite = document.createElement('div');
      invite.className = 'social-invite';
      invite.textContent = `"${state.invite}" convidou você. `;
      invite.append(
        button('aceitar', () => this.handlers.onAction(SocialAction.GuildAccept, '')),
      );
      this.root.append(invite);
    }

    this.root.append(this.friendSection(state), this.guildSection(state));
  }

  private friendSection(state: SocialPacket): HTMLElement {
    const section = document.createElement('div');
    section.className = 'social-section';

    const title = document.createElement('h4');
    title.className = 'social-heading';
    title.textContent = `Amigos (${state.friends.filter((f) => f.online).length}/${state.friends.length})`;
    section.append(title);

    for (const friend of state.friends) {
      const row = document.createElement('div');
      row.className = `social-row ${friend.online ? 'online' : 'offline'}`;
      row.append(dot(friend.online), text(friend.name));
      row.append(
        button('×', () => this.handlers.onAction(SocialAction.RemoveFriend, friend.name), 'ghost'),
      );
      section.append(row);
    }

    section.append(
      form('adicionar amigo', (name) => this.handlers.onAction(SocialAction.AddFriend, name)),
    );
    return section;
  }

  private guildSection(state: SocialPacket): HTMLElement {
    const section = document.createElement('div');
    section.className = 'social-section';

    const title = document.createElement('h4');
    title.className = 'social-heading';
    title.textContent = state.guild ? `Guilda: ${state.guild}` : 'Guilda';
    section.append(title);

    if (!state.guild) {
      section.append(
        form('fundar guilda', (name) => this.handlers.onAction(SocialAction.GuildCreate, name)),
      );
      return section;
    }

    const isLeader = state.members.some(
      (member) => member.rank === GuildRank.Leader && member.online,
    );

    for (const member of state.members) {
      const row = document.createElement('div');
      row.className = `social-row ${member.online ? 'online' : 'offline'}`;
      row.append(dot(member.online), text(member.name));

      if (member.rank === GuildRank.Leader) {
        const badge = document.createElement('span');
        badge.className = 'social-rank';
        badge.textContent = 'líder';
        row.append(badge);
      }

      section.append(row);
    }

    // O botão de expulsar aparece para todos e é recusado no servidor para quem não lidera.
    // A checagem daqui é conveniência, não segurança — quem manda é sempre o servidor.
    if (isLeader) {
      section.append(
        form('convidar', (name) => this.handlers.onAction(SocialAction.GuildInvite, name)),
        form('expulsar', (name) => this.handlers.onAction(SocialAction.GuildKick, name)),
      );
    }

    section.append(
      button('sair da guilda', () => this.handlers.onAction(SocialAction.GuildLeave, '')),
    );
    return section;
  }
}

function dot(online: boolean): HTMLElement {
  const element = document.createElement('span');
  element.className = `social-dot ${online ? 'on' : 'off'}`;
  return element;
}

function text(value: string): HTMLElement {
  const element = document.createElement('span');
  element.className = 'social-name';
  element.textContent = value;
  return element;
}

function button(label: string, onClick: () => void, extra = ''): HTMLButtonElement {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = `commerce-button${extra ? ` ${extra}` : ''}`;
  element.textContent = label;
  element.addEventListener('click', onClick);
  return element;
}

/** Campo + botão. O `form` existe para o Enter valer, que é como se digita um nome. */
function form(label: string, onSubmit: (name: string) => void): HTMLElement {
  const element = document.createElement('form');
  element.className = 'social-form';

  const field = document.createElement('input');
  field.type = 'text';
  field.className = 'social-input';
  field.placeholder = label;
  field.maxLength = 32;
  field.autocomplete = 'off';

  const submit = document.createElement('button');
  submit.type = 'submit';
  submit.className = 'commerce-button';
  submit.textContent = 'ok';

  element.addEventListener('submit', (event) => {
    event.preventDefault();

    const name = field.value.trim();
    field.value = '';
    if (name.length > 0) onSubmit(name);
  });

  element.append(field, submit);
  return element;
}
